import {none, some} from '@danwang/option';
import {useAllAccountsQuery} from '@transactions/graphql';
import * as React from 'react';
import AccountSelect from 'src/AccountSelect';
import {SegmentedButton} from 'src/SegmentedButton';
import type {Option} from '@danwang/option';
import type {AccountListItemFragment} from '@transactions/graphql';

type Props = {
  /** Selected credit account id, or null when the benefit refunds to the card. */
  value: string | null;
  onChange: (accountId: string | null) => void;
  /** The benefit's own card — excluded from the picker (that's the refund case). */
  cardAccountId: string;
};

/**
 * How a benefit's credit arrives: refunded on the card itself (a statement
 * credit picked up from the card feed) or loaded onto a separate account
 * (e.g. Uber Cash), which enables one-click credit recording.
 */
export const CreditDestinationField = ({value, onChange, cardAccountId}: Props) => {
  const [loadedToOther, setLoadedToOther] = React.useState(value != null);
  const {data} = useAllAccountsQuery({skip: !loadedToOther});

  const selectedAccount: Option<AccountListItemFragment> = React.useMemo(() => {
    const match = data?.allAccounts.find((a) => a._id === value);
    return match ? some(match) : none;
  }, [data, value]);

  const handleSelect = (account: Option<AccountListItemFragment>) => {
    onChange(account.map((a) => a._id).getOrElse(null));
  };

  return (
    <div className="flex flex-col gap-2">
      <label className="text-sm font-medium">Credit destination</label>
      <SegmentedButton
        className="self-start"
        options={[
          {value: 'card', label: 'Refunded on this card'},
          {value: 'other', label: 'Loaded to another account'},
        ]}
        value={loadedToOther ? 'other' : 'card'}
        onChange={(next) => {
          if (next === 'card') {
            setLoadedToOther(false);
            onChange(null);
          } else {
            setLoadedToOther(true);
          }
        }}
      />
      {loadedToOther ? (
        <>
          <AccountSelect
            selectedAccount={selectedAccount}
            onSelectAccount={handleSelect}
            excludeIds={[cardAccountId]}
            // Benefit credits post as USD raws in the destination account.
            currency="USD"
          />
          <p className="text-muted-foreground text-xs">
            Credits arrive on this account (e.g. Uber Cash) and can be recorded
            with one click from the Inbox
          </p>
        </>
      ) : (
        <p className="text-muted-foreground text-xs">
          Credits post back to the card and are linked from its transaction
          feed
        </p>
      )}
    </div>
  );
};
